import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import {
  HOTEL_SEARCH_TYPEAHEAD_TYPES,
  getLocationSearchErrorMessage,
} from 'constants/search'
import {
  getPlaceSelectSearchResults,
  shouldUseStreamingPlaceTypeahead,
} from 'lib/search/placeSelectTypeahead'
import { getSearchItemImageId } from 'lib/search/searchItem'
import { createExperienceNaturalLanguageItem } from 'lib/search/experienceNaturalLanguageSearch'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import { useLocalizedSearchItems, usePlacesBySearchPhrase } from 'hooks/usePlace'
import { useTypeaheadStreaming } from 'hooks/Search/useTypeaheadStreaming'
import uuidV4 from 'lib/string/uuidV4Utils'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import styled from 'styled-components'
import { take } from 'lib/array/arrayUtils'
import clsx from 'clsx'
import ListItem from 'LuxKit/List/ListItem'
import ListSubheader from 'LuxKit/List/ListSubheader'
import { connect } from 'react-redux'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import SearchTextListItem from '../SearchTextListItem/SearchTextListItem'
import VerticalSpacer from '../../Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from '../../utils/breakpoint'
import {
  createNaturalLanguageItem,
  getTypeaheadSuggestionsV2,
} from 'lib/search/searchUtils'
import { prependCanonicalExperienceDestination } from 'lib/search/canonicalExperienceDestination'
import { excludeNullOrUndefined } from 'checkout/utils'
import { nlsTypeaheadSuggestedClicked } from 'analytics/snowplow/events'

const ResultContainer = styled.div`
  position: relative;
  min-height: ${rem(80)};
`

const Separator = styled.div`
  margin-top: ${rem(24)};
`

const ItemContainer = styled.div`
  &.horizontal {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }

  &.useImage {
    display: flex;
    column-gap: ${rem(40)};
    flex-wrap: wrap;
  }
`

const NaturalLanguageSearchDefaultSuggestions = styled.div`
  padding-top: ${rem(16)};
  ${mediaQueryUp.tablet} {
    padding-top: 0;
  }
`

interface MappedProps {
  canViewLuxPlusBenefits: boolean
  currentRegionCode: string
}

interface Props extends MappedProps {
  className?: string
  searchPhrase?: string
  searchTypes?: Array<App.SearchType>
  placeTypes?: Array<App.SearchPlaceType>
  onChange?: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
  ) => void
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string, shouldTrack?: boolean, loading?: boolean) => void
  vertical?: App.SearchPlaceVertical
  overrideSearchItems?: Array<App.SearchItem>
  selectedDropdown?: number
  showHorizontalLayout?: boolean
  useImage?: boolean
  allowExperienceNaturalLanguageSearch?: boolean
  showNaturalLanguageSearchSuggestions?: boolean
  /** Temporary: Ref to expose typeaheadId and searchPhrase for search submission */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
  /** Temporary: TypeaheadId to use for streaming (passed from parent to ensure consistency) */
  temporaryTypeaheadId?: string
}

function SearchPlaceSelectList(props: Props) {
  const intl = useIntl()
  const {
    className,
    searchPhrase = '',
    onChange = noop,
    placeTypes,
    searchTypes,
    onResultsChange = noop,
    overrideSearchItems,
    vertical,
    selectedDropdown,
    showHorizontalLayout = false,
    canViewLuxPlusBenefits,
    currentRegionCode,
    useImage,
    allowExperienceNaturalLanguageSearch = false,
    showNaturalLanguageSearchSuggestions = false,
    temporarySearchSubmitRef,
    temporaryTypeaheadId,
  }: Props = props

  const isHotelSearch = placeTypes == HOTEL_SEARCH_TYPEAHEAD_TYPES
  const useStreamingTypeahead = shouldUseStreamingPlaceTypeahead(
    showNaturalLanguageSearchSuggestions,
    placeTypes,
  )
  const priority = isHotelSearch ? 'next' : 'current'
  const limit = vertical === 'tour' ? 10 : 9

  // Temporary: Use typeaheadId from parent if provided, otherwise generate one
  // This ensures we use the same typeaheadId that's stored in the ref
  // Note: The parent (SearchPlaceSelect) is responsible for setting the ref,
  // so we don't set it here to avoid conflicts
  const typeaheadId = useMemo(() => temporaryTypeaheadId ?? uuidV4(), [temporaryTypeaheadId])

  const { dropdownData, naturalLanguageItemData, cancelDebounce } = useTypeaheadStreaming(
    useStreamingTypeahead ? searchPhrase : '',
    typeaheadId,
    {
      enabled: useStreamingTypeahead && !overrideSearchItems,
      types: placeTypes,
      limit,
      isLuxPlusMember: canViewLuxPlusBenefits,
      inputThreshold: 1,
    },
  )

  // Update the ref with cancelDebounce so submit can cancel pending debounced requests
  // We include searchPhrase in dependencies to ensure this runs on every keystroke,
  // after the parent has had a chance to initialize the ref
  useEffect(() => {
    if (temporarySearchSubmitRef?.current && (showNaturalLanguageSearchSuggestions || allowExperienceNaturalLanguageSearch)) {
      temporarySearchSubmitRef.current.cancelDebounce = cancelDebounce
    }
  }, [temporarySearchSubmitRef, cancelDebounce, showNaturalLanguageSearchSuggestions, allowExperienceNaturalLanguageSearch, searchPhrase])

  const [traditionalSearchResults, traditionalLoading] = usePlacesBySearchPhrase(searchPhrase, {
    disabled: !!overrideSearchItems || useStreamingTypeahead,
    placeTypes,
    searchTypes,
    vertical,
    priority,
    limit,
    canViewLuxPlusBenefits,
  })

  // Extract results from streaming or traditional search
  const searchResults = useMemo(() => getPlaceSelectSearchResults(
    useStreamingTypeahead,
    dropdownData?.results,
    traditionalSearchResults,
  ), [useStreamingTypeahead, dropdownData?.results, traditionalSearchResults])

  // Loading state - used to determine if Enter key should select
  // We want loading = false when there are results available to select
  const hasDropdownData = dropdownData !== undefined
  const loading = useMemo(() => {
    if (useStreamingTypeahead) {
      return !dropdownData?.streamingError && !hasDropdownData
    }
    return traditionalLoading
  }, [useStreamingTypeahead, dropdownData?.streamingError, hasDropdownData, traditionalLoading])

  const tourResults = useMemo(() => {
    if (!overrideSearchItems) {
      return searchResults.filter(result => result.searchType === 'tour')
    }
    return overrideSearchItems.filter(result => result.searchType === 'tour')
  }, [overrideSearchItems, searchResults])

  const searchResultAmount = useImage ? 9 : (vertical === 'tour' || showHorizontalLayout) ? 6 : 8

  const naturalLanguageSuggestions = useMemo(() => {
    if (dropdownData?.naturalLanguageSuggestions && dropdownData.naturalLanguageSuggestions.length > 0) {
      // Suggestions can arrive before extraction finishes. Keep extracting:true until isComplete
      // so submitting one re-runs extraction instead of searching on partial data.
      if (dropdownData.isComplete) {
        return dropdownData.naturalLanguageSuggestions
      }
      return dropdownData.naturalLanguageSuggestions.map(suggestion => ({
        ...suggestion,
        searchItem: { ...suggestion.searchItem, extracting: true },
      }))
    }
    return getTypeaheadSuggestionsV2(currentRegionCode)
  }, [currentRegionCode, dropdownData?.naturalLanguageSuggestions, dropdownData?.isComplete])

  const firstNaturalLanguageResult = useMemo(() => {
    if (allowExperienceNaturalLanguageSearch && searchPhrase.trim()) {
      return createExperienceNaturalLanguageItem(searchPhrase.trim(), typeaheadId)
    }
    if (showNaturalLanguageSearchSuggestions && searchPhrase && naturalLanguageItemData) {
      return createNaturalLanguageItem(searchPhrase, naturalLanguageItemData)
    }
    return undefined
  }, [showNaturalLanguageSearchSuggestions, allowExperienceNaturalLanguageSearch, searchPhrase, naturalLanguageItemData, typeaheadId])

  const unlocalizedFinalResults = useMemo<Array<App.SearchItem>>(() => {
    if (!overrideSearchItems) {
      const canonicalizedSearchResults = prependCanonicalExperienceDestination(searchResults, searchPhrase, vertical)
      const results = take(canonicalizedSearchResults.filter(result => result.searchType !== 'tour'), searchResultAmount)

      if (allowExperienceNaturalLanguageSearch && dropdownData?.resultsInput === searchPhrase && results.length === 0 && firstNaturalLanguageResult) {
        return [firstNaturalLanguageResult]
      }
      if (showNaturalLanguageSearchSuggestions) {
        return [
          firstNaturalLanguageResult,
          ...take(naturalLanguageSuggestions, 2),
          ...results,
        ].filter(excludeNullOrUndefined)
      }
      return results
    }
    return overrideSearchItems.filter(result => {
      const normalisedSearch = searchPhrase.toLowerCase()
      return result.format.mainText.toLowerCase().includes(normalisedSearch) ||
        result.format.secondaryText?.toLowerCase().includes(normalisedSearch)
    })
  }, [overrideSearchItems, searchResults, searchPhrase, vertical, searchResultAmount, showNaturalLanguageSearchSuggestions, firstNaturalLanguageResult, naturalLanguageSuggestions, allowExperienceNaturalLanguageSearch, dropdownData?.resultsInput])
  const finalResults = useLocalizedSearchItems(unlocalizedFinalResults)

  // Keep previous results visible while loading new ones
  const [localFinalResults, setLocalFinalResults] = useState<Array<App.SearchItem>>([])
  const [localTourResults, setLocalTourResults] = useState<Array<App.SearchItem>>([])

  useEffect(() => {
    setLocalFinalResults(finalResults)
    setLocalTourResults(tourResults)
  }, [finalResults, tourResults])

  // Track when loading completes to trigger analytics (once per search)
  const prevLoadingRef = useRef(loading)
  const hasTrackedRef = useRef(false)

  // Reset tracking when search phrase changes
  useEffect(() => {
    hasTrackedRef.current = false
  }, [searchPhrase])

  useEffect(() => {
    const wasLoading = prevLoadingRef.current
    const loadingCompleted = wasLoading && !loading

    // Only track once per search when loading completes
    const shouldTrack = loadingCompleted && !hasTrackedRef.current

    if (shouldTrack) {
      hasTrackedRef.current = true
    }

    onResultsChange(finalResults, searchPhrase, shouldTrack, loading)

    prevLoadingRef.current = loading
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [finalResults, loading, searchPhrase])

  const naturalLanguageResults = useMemo(() => {
    return localFinalResults.filter(item => item.searchType === 'searchItemV3').filter(result => result.display.searchType === 'naturalLanguage')
  }, [localFinalResults])

  const nonNaturalLanguageResults = useMemo(() => {
    return localFinalResults.filter(result => {
      if (result.searchType === 'searchItemV3') {
        return result.display.searchType !== 'naturalLanguage'
      }
      return true
    })
  }, [localFinalResults])

  // Holds the previous verdict while the next keystroke resolves (like localFinalResults above),
  // only updating once resultsInput confirms the data is for the phrase on screen.
  const [hasOsTypeaheadResults, setHasOsTypeaheadResults] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    if (showNaturalLanguageSearchSuggestions && dropdownData?.resultsInput === searchPhrase) {
      setHasOsTypeaheadResults((dropdownData.results?.length ?? 0) > 0)
    }
  }, [showNaturalLanguageSearchSuggestions, dropdownData?.resultsInput, dropdownData?.results, searchPhrase])

  const handleOnClick = useCallback((searchItem?: App.SearchItem) => {
    onChange(searchItem)
  }, [onChange])

  const handleNaturalLanguageClick = useCallback((searchItem?: App.SearchItem) => {
    if (searchItem) {
      nlsTypeaheadSuggestedClicked('search_results', 'select')
    }
    handleOnClick(searchItem)
  }, [handleOnClick])

  const isInitialLoad = loading && localFinalResults.length === 0

  return <ResultContainer className={className}>
    {isInitialLoad && !useStreamingTypeahead && (
      <LoadingIndicator visible={loading} opaque floating>
        <BodyText variant="large" weight="bold" colour="normal-contrast">
          {intl.formatMessage({
            id: 'Jp6BpwDA9ymopoZ3bHiR',
            defaultMessage: 'Loading',
            description: 'Loading indicator text displayed while page content is being fetched or rendered',
          })}
        </BodyText>
      </LoadingIndicator>
    )}
    <VerticalSpacer gap={24}>
      {allowExperienceNaturalLanguageSearch && naturalLanguageResults.length === 1 && naturalLanguageResults[0].value === searchPhrase.trim() && (
        <SearchTextListItem
          searchItem={naturalLanguageResults[0]}
          isHighlighted
          onClick={handleOnClick}
        />
      )}

      {showNaturalLanguageSearchSuggestions && firstNaturalLanguageResult && hasOsTypeaheadResults === false && (
        <NaturalLanguageSearchDefaultSuggestions>
          <SearchTextListItem
            searchItem={firstNaturalLanguageResult}
            isHighlighted
            onClick={handleNaturalLanguageClick}
          />
          {naturalLanguageResults.slice(1).map((suggestion, index) =>
            <SearchTextListItem
              key={`${suggestion.value}_${index}`}
              searchItem={suggestion}
              boldText={searchPhrase}
              onClick={handleNaturalLanguageClick}
            />)}
        </NaturalLanguageSearchDefaultSuggestions>
      )}

      {!isInitialLoad && (
        <>
          {!showNaturalLanguageSearchSuggestions && !allowExperienceNaturalLanguageSearch && (
            <>
              {searchPhrase.length > 0 && localFinalResults.length === 0 && localTourResults.length === 0 && <ListItem
                title={intl.formatMessage(
                  {
                    id: 'ace9mkDuUniH6ixoylg7',
                    defaultMessage: 'Sorry, we couldn’t find <b>{searchPhrase}</b>.',
                    description: 'Error message when search phrase returns no results',
                  },
                  {
                    searchPhrase,
                    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                  },
                )}
                subtitle={getLocationSearchErrorMessage(intl)}
              />}
              {searchPhrase.length === 0 && localFinalResults.length === 0 && localTourResults.length === 0 && <ListItem
                title={intl.formatMessage({
                  id: 'UsVbtMXZDWtyOFQU-N7l',
                  defaultMessage: 'Sorry, we couldn’t find a match.',
                  description: 'Error message when no search phrase returns no results',
                })}
                subtitle={getLocationSearchErrorMessage(intl)}
              />}
            </>
          )}
          {searchPhrase.length > 0 && <ItemContainer className={clsx({ horizontal: showHorizontalLayout, useImage })}>
            {nonNaturalLanguageResults.map((searchItem, index) => <SearchMenuListItem
              key={`${searchItem.value}_${index}`}
              searchItem={searchItem}
              onClick={handleOnClick}
              searchPhrase={searchPhrase}
              selected={index === selectedDropdown}
              horizontal={showHorizontalLayout}
              searchVertical={vertical}
              imageId={getSearchItemImageId(searchItem)}
              useImage={useImage}
            />)}
          </ItemContainer>}
        </>
      )}
    </VerticalSpacer>

    {!isInitialLoad && localTourResults.length > 0 && <Separator>
      <ListSubheader>
        {intl.formatMessage(
          {
            id: 'H7mVd_l3NxYDEQO3eyct',
            defaultMessage: 'Related {count, plural, one {# tour} other {# tours}}',
            description: 'Header for related tours section with count on the tour page',
          },
          { count: localTourResults.length },
        )}
      </ListSubheader>
      <ItemContainer className={clsx({ horizontal: showHorizontalLayout })}>
        {localTourResults.map((searchItem, index) => <SearchMenuListItem
          key={`${searchItem.value}_${index}`}
          searchItem={searchItem}
          onClick={handleOnClick}
          searchPhrase={searchPhrase}
        />)}
      </ItemContainer>
    </Separator>}
  </ResultContainer>
}
const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
  currentRegionCode: state.geo.currentRegionCode,
})

export default connect(mapStateToProps)(SearchPlaceSelectList)
